import { z } from "zod";

export { canonicalTimeZone, TIME_ZONE_ALIASES, TIME_ZONES } from "./timezones.ts";

/** Largest file that can be uploaded (Files, chat attachments). The ingress allows the same (krut.maxUploadSize). */
export const MAX_UPLOAD_MB = 50;

/**
 * How far knowledge made in a person's assistant may travel: not at all, to the rest of their group,
 * or on to the whole company. The group's own rules say what stays with the group; the company rules
 * say what may go company-wide.
 */
export const SHARE_LEVELS = ["off", "group", "company"] as const;
export type ShareLevel = (typeof SHARE_LEVELS)[number];
/** Stored values from before the tiers were booleans: true meant company-wide, false meant off. */
const shareLevel = (fallback: ShareLevel) =>
  z.preprocess((v) => (v === true ? "company" : v === false ? "off" : v), z.enum(SHARE_LEVELS).default(fallback));

/**
 * What a group's members may give their agent. Every flag is enforced by Krut
 * when it renders the user's Hermes config or pod spec — never by trusting the agent.
 * Network-level internet blocking was deliberately dropped (no policy engine on the cluster).
 */
export const permissionsSchema = z.object({
  /** Running commands and code in their own sandbox, with every tool installed there. */
  terminal: z.boolean().default(true),
  browser: z.boolean().default(false),
  webTools: z.boolean().default(true),
  /** Spawning subagents (delegate_task): each one is another model conversation, so it multiplies cost. */
  subagents: z.boolean().default(true),
  fileUpload: z.boolean().default(true),
  cronJobs: z.boolean().default(true),
  /** Composio toolkit slugs, or ["*"] for all. */
  composioToolkits: z.array(z.string()).default([]),
  /** Which chat apps members may reach their assistant from (Krut's router bots). */
  messaging: z
    .object({ slack: z.boolean().default(true), discord: z.boolean().default(true), whatsapp: z.boolean().default(true) })
    .default({ slack: true, discord: true, whatsapp: true }),
  /** Skills their assistants make: kept to themselves, shared within the group, or also company-wide. */
  publishSkills: shareLevel("company"),
  /** Memories their assistants form: likewise. */
  promoteMemory: shareLevel("company"),
  /** Their assistant may send requests to colleagues' assistants and receive theirs. */
  colleagueRequests: z.boolean().default(true),
  /** Adding their own private MCP servers (company-wide ones are available to everyone regardless). */
  customMcp: z.boolean().default(true),
  /** Their assistant may build web apps and publish them at a link (behind sign-in, private to the maker until shared). */
  webApps: z.boolean().default(true),
  /** Their apps may also be opened by people outside the workspace: any Google account, or anyone with a share link. */
  publicApps: z.boolean().default(true),
});
export type Permissions = z.infer<typeof permissionsSchema>;

export const quotasSchema = z.object({
  /** Hard monthly LLM spend cap in USD; null = unlimited. */
  monthlyBudgetUsd: z.number().nonnegative().nullable().default(20),
  storageGb: z.number().int().min(1).max(1024).default(10),
  memoryMb: z.number().int().min(256).max(16384).default(1024),
  /** Memory for the shell sandbox — user code that OOMs dies here, not the agent. */
  sandboxMemoryMb: z.number().int().min(256).max(16384).default(1024),
  /** Memory for the person's published web apps, all together (their own container). */
  appsMemoryMb: z.number().int().min(128).max(16384).default(512),
});
export type Quotas = z.infer<typeof quotasSchema>;

export const groupInputSchema = z.object({
  name: z.string().trim().min(1).max(64),
  description: z.string().max(500).default(""),
  quotas: quotasSchema,
  permissions: permissionsSchema,
  isDefault: z.boolean().default(false),
});
export type GroupInput = z.infer<typeof groupInputSchema>;

/* ── Group knowledge: what stays with the group ───────────────────────── */

export const DEFAULT_GROUP_MEMORY_RULES = `Keep a memory within the group (rather than sharing it with the whole company) when it is mainly useful to this team, for example:
- how the team works: its own processes, conventions, tools, templates and routines;
- the team's current work: projects, tasks, deadlines, who is doing what;
- details of the team's clients, suppliers or systems that other teams don't deal with;
- drafts, plans and decisions that are still being worked out within the team.

Share with the whole company only what colleagues outside the team would also need: company-wide facts, products, pricing, policies, and decisions that affect everyone.`;

export const DEFAULT_GROUP_SKILL_RULES = `Keep a skill within the group (rather than sharing it with the whole company) when it is about this team's own work, for example:
- steps that only make sense with the team's own tools, systems, accounts or folders;
- the team's own reports, templates, checklists and routines;
- how to handle the team's clients, suppliers or projects.

Share with the whole company only skills that any colleague could use as they are: general how-to, company-wide tools and processes.`;

/** The group's own sharing rules — the prompts that keep team knowledge with the team. Empty = the defaults. */
export const groupKnowledgeSchema = z.object({
  memoryRules: z.string().max(8000).default(DEFAULT_GROUP_MEMORY_RULES),
  skillRules: z.string().max(8000).default(DEFAULT_GROUP_SKILL_RULES),
});
export type GroupKnowledge = z.infer<typeof groupKnowledgeSchema>;

/** Everything the group's Knowledge page saves: the two sharing levels and the two prompts. */
export const groupKnowledgeInputSchema = groupKnowledgeSchema.extend({
  promoteMemory: z.enum(SHARE_LEVELS),
  publishSkills: z.enum(SHARE_LEVELS),
});
export type GroupKnowledgeInput = z.infer<typeof groupKnowledgeInputSchema>;

/** Which colleagues a shared memory or skill reaches. */
export const AUDIENCES = ["group", "company"] as const;
export type Audience = (typeof AUDIENCES)[number];

/* ── Briefings: the assistant reaches out on its own ───────────────────── */

export const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;
/** "HH:MM" local times, 1–3 a day, kept sorted and unique. */
export const briefingTimesSchema = z
  .array(z.string().regex(TIME_OF_DAY, "Use HH:MM, e.g. 10:00"))
  .min(1)
  .max(3)
  .transform((t) => [...new Set(t)].sort());

export const DEFAULT_BRIEFING_FOCUS = `Help each person start their day with a clear, short plan. Focus on what moves their work forward: replies they are waiting for or owe, deadlines and meetings to prepare, and numbers that changed noticeably since the last briefing.
Bring in what is happening in the company (from shared memory) only when it affects them.
When nothing much changed, say so in a few lines — never pad. End with one concrete thing you could take off their plate today.`;

/** Company-wide briefing settings (admin → Assistants → Briefings). */
export const briefingSettingsSchema = z.object({
  /** The daily briefing runs for groups that have it on. On by default, so a new install shows what it does. */
  enabled: z.boolean().default(true),
  /** New workspaces get a "getting started" conversation from their assistant. */
  onboarding: z.boolean().default(true),
  /** What the company wants briefings to pay attention to; goes into every briefing turn. */
  focus: z.string().max(4000).default(DEFAULT_BRIEFING_FOCUS),
  /** Since when onboarding has been on: only workspaces created after this get it automatically (admins can send it to everyone else). */
  onboardingSince: z.string().nullable().default(null),
  /** The weekly retrospective (and goals check-in) runs for groups that have it on. */
  weekly: z.boolean().default(true),
});
export type BriefingSettings = z.infer<typeof briefingSettingsSchema>;
export const briefingSettingsInputSchema = briefingSettingsSchema.omit({ onboardingSince: true });

/** 0 = Sunday … 6 = Saturday. */
export const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
const weekdaySchema = z.number().int().min(0).max(6);
const timeOfDaySchema = z.string().regex(TIME_OF_DAY, "Use HH:MM, e.g. 16:00");

/** A group's briefing schedule: the default for its members. */
export const groupBriefingSchema = z.object({
  enabled: z.boolean().default(true),
  times: briefingTimesSchema.default(["10:00"]),
  weekdaysOnly: z.boolean().default(true),
  /** The weekly retrospective: on, which day and what time (local to each person). Friday 16:00 by default. */
  weeklyEnabled: z.boolean().default(true),
  weeklyDay: weekdaySchema.default(5),
  weeklyTime: timeOfDaySchema.default("16:00"),
});
export type GroupBriefing = z.infer<typeof groupBriefingSchema>;

/** A person's own choice: muted, or their own times instead of the group's — for the daily briefing and the weekly retrospective. */
export const userBriefingSchema = z.object({
  muted: z.boolean().default(false),
  times: briefingTimesSchema.nullable().default(null),
  weeklyMuted: z.boolean().default(false),
  weeklyDay: weekdaySchema.nullable().default(null),
  weeklyTime: timeOfDaySchema.nullable().default(null),
});
export type UserBriefing = z.infer<typeof userBriefingSchema>;

export const BRIEFING_KINDS = ["onboarding", "briefing", "weekly"] as const;
export type BriefingKind = (typeof BRIEFING_KINDS)[number];
export const BRIEFING_RUN_STATUSES = ["running", "sent", "failed", "skipped"] as const;
export type BriefingRunStatus = (typeof BRIEFING_RUN_STATUSES)[number];

/* ── Goals: what each person is working towards ───────────────────────── */

export const GOAL_STATUSES = ["active", "done", "dropped"] as const;
export type GoalStatus = (typeof GOAL_STATUSES)[number];
// A real calendar day: JavaScript would quietly roll 2027-02-30 over to 2 March, so compare the round trip.
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD")
  .refine((d) => {
    const t = Date.parse(`${d}T00:00:00Z`);
    return !Number.isNaN(t) && new Date(t).toISOString().slice(0, 10) === d;
  }, "Not a real date");

/** A goal as the person (web) or their assistant (tools) creates it. */
export const goalInputSchema = z.object({
  title: z.string().trim().min(2).max(200),
  /** What success looks like, why it matters. */
  detail: z.string().trim().max(2000).default(""),
  /** Target date, YYYY-MM-DD; null = no deadline. */
  deadline: isoDate.nullable().default(null),
});
export type GoalInput = z.infer<typeof goalInputSchema>;

/** A change to a goal; a progress note is kept in the goal's history. */
export const goalPatchSchema = z
  .object({
    title: z.string().trim().min(2).max(200),
    detail: z.string().trim().max(2000),
    deadline: isoDate.nullable(),
    status: z.enum(GOAL_STATUSES),
    /** 0–100, how far along. */
    progress: z.number().int().min(0).max(100),
    /** What happened, in a sentence or two. */
    note: z.string().trim().min(1).max(1000),
  })
  .partial();
export type GoalPatch = z.infer<typeof goalPatchSchema>;

/* ── Time saved: finished tasks the assistants record (only their type) ── */

export const TASK_TYPES = ["writing", "research", "data", "coding", "admin", "briefing"] as const;
export type TaskType = (typeof TASK_TYPES)[number];

/** What each type covers (the assistant picks from these) and its standard time until an admin changes it. */
export const TASK_TYPE_INFO: Record<TaskType, { label: string; covers: string; minutes: number }> = {
  writing: { label: "Writing", covers: "emails, documents, posts and summaries", minutes: 20 },
  research: { label: "Research", covers: "finding, reading and comparing information", minutes: 30 },
  data: { label: "Data & analysis", covers: "spreadsheets, numbers, analysis and charts", minutes: 45 },
  coding: { label: "Code & apps", covers: "scripts, automations, web apps and fixes", minutes: 60 },
  admin: { label: "Admin", covers: "scheduling, organising, forms and follow-ups", minutes: 10 },
  briefing: { label: "Briefings", covers: "daily briefings, digests and regular updates", minutes: 15 },
};

/** The answers to "did it save you time?", in minutes ("2 h+" counts as 2 hours). */
export const SAVED_ANSWERS = [
  { minutes: 0, label: "None" },
  { minutes: 15, label: "15 min" },
  { minutes: 60, label: "1 h" },
  { minutes: 120, label: "2 h+" },
] as const;
export const savedAnswerSchema = z.literal(SAVED_ANSWERS.map((a) => a.minutes));

const standardMinutes = (t: TaskType) => z.number().int().min(0).max(480).default(TASK_TYPE_INFO[t].minutes);
/** How long each type of task would take someone by hand, in minutes (admin → Workspace → Hours saved). */
export const timeSavedSettingsSchema = z.object({
  minutes: z
    .object({
      writing: standardMinutes("writing"),
      research: standardMinutes("research"),
      data: standardMinutes("data"),
      coding: standardMinutes("coding"),
      admin: standardMinutes("admin"),
      briefing: standardMinutes("briefing"),
    })
    .prefault({}),
});
export type TimeSavedSettings = z.infer<typeof timeSavedSettingsSchema>;

/**
 * The record_finished_task tool answers with this mark and the task's number. It stays in the chat's saved
 * tool output (never in the reply), which is how the chat finds the task to ask about.
 */
export const TASK_REF = "task-ref";
export const taskRefs = (toolOutput: string) => [...toolOutput.matchAll(/\btask-ref (\d+)\b/g)].map((m) => Number(m[1]));

export const SSO_PROVIDERS = ["google", "microsoft"] as const;
export type SsoProviderId = (typeof SSO_PROVIDERS)[number];

export const ssoProviderInputSchema = z.object({
  enabled: z.boolean(),
  clientId: z.string().trim().min(1),
  /** Omit to keep the stored secret. */
  clientSecret: z.string().trim().min(1).optional(),
  /** Microsoft only: tenant id, or "common" / "organizations". */
  tenantId: z.string().trim().optional(),
});
export type SsoProviderInput = z.infer<typeof ssoProviderInputSchema>;

export const signupPolicySchema = z.object({
  /** "invite" = only users an admin created may sign in; "domain" = anyone from allowedDomains is auto-created. */
  mode: z.enum(["invite", "domain"]).default("invite"),
  allowedDomains: z.array(z.string().trim().toLowerCase().min(1)).default([]),
});
export type SignupPolicy = z.infer<typeof signupPolicySchema>;

/**
 * Email + password sign-in next to SSO, off by default. It never creates accounts: someone an admin added (or
 * who signed in before) sets or resets their password only after a code sent to their email.
 */
export const passwordSignInSchema = z.object({ enabled: z.boolean().default(false) });
export type PasswordSignIn = z.infer<typeof passwordSignInSchema>;
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;

export const ROLES = ["admin", "user"] as const;
export type Role = (typeof ROLES)[number];
export const USER_STATUSES = ["active", "disabled"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const userInputSchema = z.object({
  email: z.email().trim().toLowerCase(),
  name: z.string().trim().max(120).default(""),
  role: z.enum(ROLES).default("user"),
  groupId: z.string().nullable().default(null),
  status: z.enum(USER_STATUSES).default("active"),
});
export type UserInput = z.infer<typeof userInputSchema>;
/**
 * Only the fields sent. Not `userInputSchema.partial()`: Zod 4 still applies the defaults of optional
 * fields, so changing the role would also reset the group to none, blank the name, and so on.
 */
export const userPatchSchema = z.object({
  name: z.string().trim().max(120).optional(),
  role: z.enum(ROLES).optional(),
  groupId: z.string().nullable().optional(),
  status: z.enum(USER_STATUSES).optional(),
});

/* ── LLM providers & model allowlist ───────────────────────────────────── */

export const LLM_PROVIDER_KINDS = ["openrouter", "openai"] as const;
/**
 * How OpenRouter picks among the many hosts of a model. "price" (OpenRouter's default) often lands on
 * slow hosts and hops between them, losing prompt-cache hits; "throughput" measured ~3–4× faster.
 */
export const LLM_ROUTINGS = ["throughput", "latency", "price"] as const;
export type LlmRouting = (typeof LLM_ROUTINGS)[number];
export type LlmProviderKindT = (typeof LLM_PROVIDER_KINDS)[number];

export const llmProviderInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  kind: z.enum(LLM_PROVIDER_KINDS),
  baseUrl: z.url().trim(),
  /** Omit to keep the stored key. */
  apiKey: z.string().trim().min(1).optional(),
  enabled: z.boolean().default(true),
  /** OpenRouter only. */
  routing: z.enum(LLM_ROUTINGS).default("throughput"),
});
export type LlmProviderInput = z.infer<typeof llmProviderInputSchema>;

export const llmModelInputSchema = z.object({
  id: z.string().trim().min(1).max(200),
  providerId: z.string().trim().min(1),
  label: z.string().trim().max(120).default(""),
  inputPerM: z.number().nonnegative().default(0),
  outputPerM: z.number().nonnegative().default(0),
  contextLength: z.number().int().positive().nullable().default(null),
});
export type LlmModelInput = z.infer<typeof llmModelInputSchema>;

/** Ordered: the first model is the default, the rest are fallbacks in order. */
export const llmModelsInputSchema = z
  .array(llmModelInputSchema)
  .min(1, "Keep at least one model")
  .max(30)
  .refine((ms) => new Set(ms.map((m) => m.id)).size === ms.length, "Each model can appear only once");

/* ── Per-user secrets ──────────────────────────────────────────────────── */

/** Names the sandbox/runtime depends on; a user secret must never shadow them. */
const RESERVED_EXACT = new Set(["PATH", "HOME", "USER", "LOGNAME", "SHELL", "PWD", "TERM", "HOSTNAME", "NPM_CONFIG_PREFIX", "TZ"]);
const RESERVED_PREFIXES = ["LD_", "SSH_", "KRUT_", "HERMES_", "API_SERVER_", "HINDSIGHT_", "TERMINAL_", "NOUS_"];

/**
 * LLM provider credentials Hermes (the pinned build) picks up from its environment. Given to the
 * assistant itself they could route AI calls around Krut's gateway — and its budgets. They are
 * still allowed in the sandbox, where e.g. the user's own Claude Code uses them on their own bill.
 */
export const ASSISTANT_DENIED_SECRETS = new Set([
  "ACTUAL_API_KEY", "AI_GATEWAY_API_KEY", "ALIBABA_CODING_PLAN_API_KEY", "ALIBABA_CODING_PLAN_CN_API_KEY", "ALIBABA_TOKEN_PLAN_API_KEY",
  "ALIBABA_TOKEN_PLAN_CN_API_KEY", "ANTHROPIC_API_KEY", "ANTHROPIC_TOKEN", "ARCEEAI_API_KEY", "AWS_ACCESS_KEY_ID", "AWS_SECRET_ACCESS_KEY",
  "AWS_SESSION_TOKEN", "AZURE_FOUNDRY_API_KEY", "CLAUDE_CODE_OAUTH_TOKEN", "COMMANDCODE_API_KEY", "COPILOT_GITHUB_TOKEN", "DASHSCOPE_API_KEY",
  "DEEPINFRA_API_KEY", "DEEPSEEK_API_KEY", "FIREWORKS_API_KEY", "GEMINI_API_KEY", "GH_TOKEN", "GITHUB_TOKEN", "GLM_API_KEY", "GMI_API_KEY",
  "GOOGLE_API_KEY", "GOOGLE_APPLICATION_CREDENTIALS", "HF_TOKEN", "KILOCODE_API_KEY", "KIMI_API_KEY", "KIMI_CN_API_KEY", "KIMI_CODING_API_KEY",
  "LM_API_KEY", "META_API_KEY", "META_MODEL_API_KEY", "MINIMAX_API_KEY", "MINIMAX_CN_API_KEY", "MODEL_API_KEY", "NEBIUS_API_KEY",
  "NEBIUS_TOKEN_FACTORY_API_KEY", "NOVITA_API_KEY", "NVIDIA_API_KEY", "OLLAMA_API_KEY", "OPENAI_API_KEY", "OPENCODE_GO_API_KEY",
  "OPENCODE_ZEN_API_KEY", "OPENROUTER_API_KEY", "QWEN_API_KEY", "RAMP_ROUTER_API_KEY", "ROUTER_API_KEY", "STEPFUN_API_KEY", "TOKENHUB_API_KEY",
  "TOKENPLAN_API_KEY", "UPSTAGE_API_KEY", "XAI_API_KEY", "XIAOMI_API_KEY", "ZAI_API_KEY", "Z_AI_API_KEY", "GROQ_API_KEY", "MISTRAL_API_KEY",
  "COHERE_API_KEY", "TOGETHER_API_KEY", "PERPLEXITY_API_KEY", "CEREBRAS_API_KEY",
]);

export function secretNameProblem(name: string, exposeToAssistant: boolean): string | null {
  if (!/^[A-Z][A-Z0-9_]{1,63}$/.test(name)) return "Use UPPER_SNAKE_CASE: letters, digits and underscores, starting with a letter.";
  if (RESERVED_EXACT.has(name) || RESERVED_PREFIXES.some((p) => name.startsWith(p))) return `${name} is a reserved name.`;
  if (exposeToAssistant && (ASSISTANT_DENIED_SECRETS.has(name) || /_(BASE_URL|API_BASE)$/.test(name))) {
    return `${name} can be used by your sandbox (commands, CLIs) but not by the assistant itself — it could bypass your organisation's AI gateway.`;
  }
  return null;
}

export const userSecretInputSchema = z
  .object({
    name: z.string().trim().toUpperCase(),
    /** Omit on update to keep the stored value. */
    value: z.string().min(1).max(8192).refine((v) => !/[\r\n]/.test(v), "Single-line values only").optional(),
    description: z.string().trim().max(200).default(""),
    exposeToAssistant: z.boolean().default(false),
  })
  .superRefine((s, ctx) => {
    const problem = secretNameProblem(s.name, s.exposeToAssistant);
    if (problem) ctx.addIssue({ code: "custom", path: ["name"], message: problem });
  });
export type UserSecretInput = z.infer<typeof userSecretInputSchema>;

/* ── Messaging connectors ──────────────────────────────────────────────── */

export const MESSAGING_PLATFORMS = ["slack", "discord", "whatsapp"] as const;
export type MessagingPlatformT = (typeof MESSAGING_PLATFORMS)[number];

export const messagingConnectorInputSchema = z.object({
  enabled: z.boolean(),
  /** Also answer @mentions in shared channels (replies are visible to the channel). DMs always work. */
  respondInChannels: z.boolean().default(false),
  /** Omit to keep stored tokens. Slack: botToken (xoxb-) + appToken (xapp-). Discord: botToken. WhatsApp: none. */
  secrets: z.record(z.string(), z.string().trim().min(1)).optional(),
});
export type MessagingConnectorInput = z.infer<typeof messagingConnectorInputSchema>;

/* ── Memory (Hindsight) settings ───────────────────────────────────────── */

export const DEFAULT_PROMOTION_RULES = `Share a memory with colleagues ONLY if it is durable, work-related knowledge that would help them, for example:
- facts about the company, its products, services, pricing, processes, tools and systems;
- facts about clients, partners and projects (names, needs, decisions, status, commitments);
- decisions made and the reasons for them; lessons learned; how-to knowledge.

NEVER share:
- anything personal: health, family, feelings, private opinions, personal plans, location, contact details;
- credentials, secrets, API keys, passwords, financial account details;
- HR matters: salaries, performance, hiring or firing of named people;
- anything the user said was private or confidential;
- facts that are only about the user's own preferences or their assistant's behaviour;
- temporary or trivial facts (what they had for lunch, a file being empty).

When you share, rewrite the fact so it stands on its own, attributes nothing to the individual user unless their role matters, and drops any personal detail.`;

export const memorySettingsSchema = z.object({
  /** Assistants also recall from the company bank. */
  companyMemoryEnabled: z.boolean().default(true),
  /** Automatically promote eligible user memories using the rules below. */
  promotionEnabled: z.boolean().default(true),
  /** Only memories formed after this moment are considered — never retroactive. */
  promotionEnabledAt: z.string().nullable().default(null),
  promotionRules: z.string().max(8000).default(DEFAULT_PROMOTION_RULES),
  /** Model Hindsight (memory extraction) and the promoter use; null = the default model. */
  memoryModel: z.string().nullable().default(null),
});
export type MemorySettings = z.infer<typeof memorySettingsSchema>;

export const memorySettingsInputSchema = memorySettingsSchema.omit({ promotionEnabledAt: true });

/** Some of the memory settings: each page saves only what it shows. No defaults, so what's missing stays as it is. */
export const memorySettingsPatchSchema = z
  .object({
    companyMemoryEnabled: z.boolean(),
    promotionEnabled: z.boolean(),
    promotionRules: z.string().max(8000),
    memoryModel: z.string().nullable(),
  })
  .partial();

/**
 * Smart routing: everyday chat runs on the default model (first in the chain); work that needs more
 * goes to a stronger task model. A small classifier model judges each new message; a turn that keeps
 * going past `escalateAfterSteps` tool steps moves up; messages starting with `keyword` go straight to
 * it; subagents and scheduled tasks always use it. The rest of the chain stays the fallback for both.
 */
export const smartRoutingSchema = z.object({
  enabled: z.boolean().default(false),
  /** The stronger model; must be one of the allowed models. */
  taskModel: z.string().nullable().default(null),
  /** Judges each new message (one word back); null = no classifier, only steps and the keyword. */
  classifierModel: z.string().nullable().default(null),
  escalateAfterSteps: z.number().int().min(1).max(50).default(4),
  keyword: z.string().trim().max(40).default("think hard"),
});
export type SmartRouting = z.infer<typeof smartRoutingSchema>;

/* ── Skills ────────────────────────────────────────────────────────────── */

export const DEFAULT_SKILL_RULES = `A skill may be shared with colleagues ONLY if it contains no sensitive data. Treat as sensitive:
- credentials of any kind: passwords, API keys, tokens, app passwords, private keys, connection strings;
- personal data about identifiable people: phone numbers, home addresses, personal email addresses, health, family or HR details;
- confidential client or financial data: contract values, bank or card details, customer lists with contact details;
- anything marked private or confidential.

NOT sensitive: general how-to steps, tool names, workflows, public company information, work email addresses used as an example sender, placeholders like <API_KEY>.`;

export const skillSettingsSchema = z.object({
  /** Share users' own skills (those in groups allowed to publish) with everyone once they pass the scan. */
  sharingEnabled: z.boolean().default(true),
  /** Clean skills wait for an admin instead of being shared straight away. */
  requireApproval: z.boolean().default(false),
  sensitivityRules: z.string().max(8000).default(DEFAULT_SKILL_RULES),
});
export type SkillSettings = z.infer<typeof skillSettingsSchema>;

/** Hermes skill names: also the folder name, so kept filesystem- and prompt-safe. */
export const skillNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, "Use lowercase letters, digits, dots, dashes and underscores.");

export const skillFilePathSchema = z
  .string()
  .min(1)
  .max(200)
  .refine((p) => !p.startsWith("/") && !p.split("/").some((s) => s === "" || s === "." || s === ".."), "Bad file path.");

export const officialSkillInputSchema = z.object({
  name: skillNameSchema,
  /** SKILL.md plus any support files, as text. SKILL.md is required. */
  files: z
    .record(skillFilePathSchema, z.string().max(512 * 1024))
    .refine((f) => typeof f["SKILL.md"] === "string" && f["SKILL.md"].trim().length > 0, "SKILL.md is required."),
});
export type OfficialSkillInput = z.infer<typeof officialSkillInputSchema>;

/**
 * "unshared" = listed for its owner and admins only, because sharing is off or the owner's group may not publish.
 * A shared (or pending) skill also has an audience: the owner's group, or the whole company.
 */
export const SKILL_STATUSES = ["official", "shared", "pending", "blocked", "private", "hidden", "superseded", "unshared"] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

/** The admin Skills list is split into these tabs; each shows skills in any of its statuses. */
export const SKILL_TABS = [
  { id: "official", label: "Official", statuses: ["official"] },
  { id: "shared", label: "Shared", statuses: ["shared"] },
  { id: "pending", label: "Needs review", statuses: ["pending"] },
  { id: "blocked", label: "Blocked", statuses: ["blocked"] },
  { id: "other", label: "Not shared", statuses: ["private", "hidden", "unshared"] },
] as const satisfies readonly { id: string; label: string; statuses: readonly SkillStatus[] }[];
export type SkillTabId = (typeof SKILL_TABS)[number]["id"];

/* ── Knowledge sync ────────────────────────────────────────────────────── */

export const SYNC_SCHEDULES = ["manual", "hourly", "daily", "weekly"] as const;
export type SyncSchedule = (typeof SYNC_SCHEDULES)[number];
export const SYNC_JOB_STATUSES = ["active", "paused", "proposed", "rejected"] as const;
export type SyncJobStatus = (typeof SYNC_JOB_STATUSES)[number];

const toolkitSlug = z.string().regex(/^[a-z0-9_-]{1,60}$/);

export const syncSourceSchema = z.discriminatedUnion("kind", [
  /** One or more apps connected under Krut's company Composio identity. */
  z.object({ kind: z.literal("composio"), toolkits: z.array(toolkitSlug).min(1).max(5) }),
  /** Any MCP server reachable from Krut. Header values are secrets (stored encrypted, never returned). */
  z.object({
    kind: z.literal("mcp"),
    url: z.string().url().max(500).refine((u) => /^https?:\/\//.test(u), "Use an http(s) URL."),
    headers: z.record(z.string().regex(/^[A-Za-z0-9-]{1,60}$/), z.string().max(4000)).optional(),
  }),
]);
export type SyncSource = z.infer<typeof syncSourceSchema>;

export const syncJobInputSchema = z.object({
  name: z.string().trim().min(1).max(80),
  source: syncSourceSchema,
  /** Plain English: what to sync and what to leave out. */
  instructions: z.string().trim().min(10).max(4000),
  schedule: z.enum(SYNC_SCHEDULES).default("daily"),
  /** Empty = everyone; otherwise only members of these groups recall what this job syncs. */
  groupIds: z.array(z.string()).max(50).default([]),
  /** Null = the default model. Needs a model good at tool use. */
  model: z.string().nullable().default(null),
  maxDocumentsPerRun: z.number().int().min(1).max(2000).default(200),
  maxCostPerRunUsd: z.number().min(0.01).max(100).default(1),
  enabled: z.boolean().default(true),
});
export type SyncJobInput = z.infer<typeof syncJobInputSchema>;

/* ── Colleague requests & approvals ─────────────────────────────────────── */

export const APPROVAL_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];
/** Where a person decided an approval. */
export const APPROVAL_CHANNELS = ["web", "slack", "discord", "whatsapp"] as const;
export type ApprovalChannel = (typeof APPROVAL_CHANNELS)[number];
export const REQUEST_STATUSES = ["open", "awaiting_approval", "answered", "declined"] as const;
export type ColleagueRequestStatus = (typeof REQUEST_STATUSES)[number];

export const approvalDecisionSchema = z.object({
  decision: z.enum(["approve", "reject"]),
  note: z.string().max(2000).optional(),
});

/* ── MCP servers ─────────────────────────────────────────────────────────── */

export const MCP_AUTH_KINDS = ["none", "headers", "oauth"] as const;
export type McpAuthKind = (typeof MCP_AUTH_KINDS)[number];
/** Company servers: one set of credentials for everyone ("shared"), or each person connects their own. */
export const MCP_CREDENTIAL_MODES = ["shared", "personal"] as const;
export type McpCredentialMode = (typeof MCP_CREDENTIAL_MODES)[number];
export const MCP_CONNECTION_STATUSES = ["pending", "connected", "expired", "error"] as const;
export type McpConnectionStatus = (typeof MCP_CONNECTION_STATUSES)[number];

const mcpHeaderName = z.string().regex(/^[A-Za-z0-9-]{1,60}$/, "Header names use letters, digits and dashes.");
export const mcpHeadersSchema = z.record(mcpHeaderName, z.string().min(1).max(4000));

export const mcpServerInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).default(""),
  url: z.string().trim().url().max(500).refine((u) => /^https?:\/\//.test(u), "Use an http(s) URL."),
  auth: z.enum(MCP_AUTH_KINDS).default("none"),
  /** Company servers only; a person's own server always uses their own credentials. */
  credentials: z.enum(MCP_CREDENTIAL_MODES).default("shared"),
  /** Header auth: the headers to fill in (e.g. Authorization). Values are given when connecting. */
  headerNames: z.array(mcpHeaderName).max(5).default([]),
  /** OAuth: a pre-registered client, for servers without dynamic client registration (e.g. GitHub). */
  oauthClientId: z.string().trim().max(300).default(""),
  /** Write-only; omitted = keep the stored one. */
  oauthClientSecret: z.string().max(1000).optional(),
  oauthScope: z.string().trim().max(500).default(""),
  enabled: z.boolean().default(true),
});
export type McpServerInput = z.infer<typeof mcpServerInputSchema>;

export const mcpConnectInputSchema = z.object({ headers: mcpHeadersSchema.optional() });

/* ── Branding (white-label) ──────────────────────────────────────────────── */

export const DEFAULT_BRAND_NAME = "Mnemio AI";
export const DEFAULT_ACCENT_COLOR = "#1f6f99";
export const DEFAULT_SIDEBAR_COLOR = "#141414";
/**
 * The defaults before the Mnemio look (name, slate sidebar, brighter blue). Installs saved them as they were;
 * stored exactly, they mean "never chosen", so they read as today's defaults.
 */
const LEGACY_BRAND_DEFAULTS = { name: "Krut AI", accentColor: "#1f7fbf", sidebarColor: "#333d4d" };
export function withCurrentBrandDefaults<T extends { name: string; accentColor: string; sidebarColor: string }>(b: T): T {
  const same = (x: string, y: string) => x.toLowerCase() === y;
  return {
    ...b,
    name: b.name === LEGACY_BRAND_DEFAULTS.name ? DEFAULT_BRAND_NAME : b.name,
    accentColor: same(b.accentColor, LEGACY_BRAND_DEFAULTS.accentColor) ? DEFAULT_ACCENT_COLOR : b.accentColor,
    sidebarColor: same(b.sidebarColor, LEGACY_BRAND_DEFAULTS.sidebarColor) ? DEFAULT_SIDEBAR_COLOR : b.sidebarColor,
  };
}
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a colour like #1f6f99.");
/** PNG, JPEG, WebP, SVG or ICO as a data URL, up to ~500 KB. */
const brandImage = z
  .string()
  .max(700_000, "Images can be up to 500 KB.")
  .regex(/^data:image\/(png|jpeg|webp|svg\+xml|x-icon|vnd\.microsoft\.icon);base64,[A-Za-z0-9+/]+=*$/, "Use a PNG, JPEG, WebP, SVG or ICO image.");

/** WCAG relative luminance of #rrggbb, 0 (black) … 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export const brandingInputSchema = z.object({
  name: z.string().trim().min(1).max(40).default(DEFAULT_BRAND_NAME),
  /** Shown on the dark sidebar and sign-in header: a light or white logo works best. */
  logo: brandImage.nullable().default(null),
  favicon: brandImage.nullable().default(null),
  /** Buttons, links, switches and focus rings. */
  accentColor: hexColor.default(DEFAULT_ACCENT_COLOR),
  /** Sidebar and primary buttons; white text sits on it, so it must be dark. */
  sidebarColor: hexColor.refine((c) => luminance(c) <= 0.2, "Pick a darker sidebar colour: white text has to stay readable on it.").default(DEFAULT_SIDEBAR_COLOR),
});
export type BrandingInput = z.infer<typeof brandingInputSchema>;
export const brandingSchema = brandingInputSchema.extend({ revision: z.number().int().default(0) });
export type Branding = z.infer<typeof brandingSchema>;

/* ── Web apps: what assistants build and publish at a link ─────────────── */

/**
 * Who may open an app: only its maker, the maker's group, everyone in the workspace (all sign in with the
 * workspace's own sign-in), any Google account (people outside the company too), or anyone holding the
 * share link (no sign-in at all). The last two need the group's publicApps switch.
 */
export const APP_ACCESS = ["owner", "group", "workspace", "google", "link"] as const;
export type AppAccess = (typeof APP_ACCESS)[number];
export const PUBLIC_APP_ACCESS: readonly AppAccess[] = ["google", "link"];

/** The name in an app's link (crm → crm.apps.example.com): one DNS label, lowercase. */
export const appNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, "Use 1–40 lowercase letters, digits and dashes (not at the start or end).");

/** A folder in the person's workspace that holds an app (and its webapp.json), e.g. apps/crm. */
export const appFolderSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/^\/home\/agent\/workspace\//, "").replace(/^\.\//, "").replace(/\/+$/, ""))
  .pipe(z.string().regex(/^[A-Za-z0-9_][A-Za-z0-9._-]*(?:\/[A-Za-z0-9_][A-Za-z0-9._-]*){0,5}$/, "A folder inside the workspace, e.g. apps/crm."));

/** A domain the person owns, connected to an app (tools.example.com). */
export const appDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((s) => s.replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, ""))
  .pipe(z.string().max(253).regex(/^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, "A domain like tools.example.com."));

export const appPatchSchema = z.object({
  name: appNameSchema.optional(),
  title: z.string().trim().min(1).max(80).optional(),
  access: z.enum(APP_ACCESS).optional(),
});
export type AppPatch = z.infer<typeof appPatchSchema>;

export const APP_DOMAIN_STATUSES = ["pending", "active", "error"] as const;
export type AppDomainStatus = (typeof APP_DOMAIN_STATUSES)[number];
